import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { Trophy, CurrencyDollar, ChartBar, Users, UsersThree, CaretDown } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_TOOLTIP_STYLE, getRankLabel, getRankColor } from "../config/uiHelpers";
import ManageTeamModal from "../components/ManageTeamModal";

const TeamPerformance = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [manageTeamBdm, setManageTeamBdm] = useState(null);
  const [selectedBdmId, setSelectedBdmId] = useState("");
  const [bdmDashboard, setBdmDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const isTeamAdmin = ["admin", "md", "manager"].includes(user?.role);
  const isBdm = user?.role === "bdm";
  const canSeeTeams = isTeamAdmin || isBdm;

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/team-performance`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTeams = useCallback(async () => {
    if (!canSeeTeams) { setTeamsLoading(false); return; }
    try {
      const res = await api.get(`/admin/team/overview`);
      setTeams(res.data || []);
      if (isBdm && res.data?.[0]) setSelectedBdmId(res.data[0].bdm_user_id);
    } catch (err) { console.error(err); }
    finally { setTeamsLoading(false); }
  }, [canSeeTeams, isBdm]);

  useEffect(() => { fetchData(); fetchTeams(); }, [fetchData, fetchTeams]);

  const fetchBdmDashboard = useCallback(async (bdmId) => {
    if (!bdmId) { setBdmDashboard(null); return; }
    setDashboardLoading(true);
    try {
      const res = await api.get(`/dashboard/bdm?bdm_user_id=${bdmId}`);
      setBdmDashboard(res.data);
    } catch (err) { console.error(err); setBdmDashboard(null); }
    finally { setDashboardLoading(false); }
  }, []);

  useEffect(() => { if (selectedBdmId) fetchBdmDashboard(selectedBdmId); }, [selectedBdmId, fetchBdmDashboard]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Team Performance</h1>
        <p className="text-base text-slate-700 mb-8">Agent leaderboard and BDM team management</p>

        {canSeeTeams && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{fontFamily: 'Outfit, sans-serif'}}>
              <UsersThree size={20} weight="duotone" style={{color: '#002FA7'}} /> BDM Teams
            </h2>
            {teamsLoading ? (
              <div className="flex justify-center py-8"><div className="loading-spinner"></div></div>
            ) : teams.length === 0 ? (
              <div className="text-sm text-slate-500 bg-white border border-slate-200 p-6">No BDM teams found.</div>
            ) : (
              <div data-testid="bdm-teams-grid" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {teams.map((t) => (
                  <div key={t.bdm_user_id} data-testid={`bdm-team-card-${t.bdm_user_id}`}
                    className={`bg-white border p-5 cursor-pointer transition-colors ${selectedBdmId === t.bdm_user_id ? 'border-[#002FA7] shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => setSelectedBdmId(t.bdm_user_id)}>
                    <div className="font-semibold text-lg" style={{fontFamily: 'Outfit, sans-serif'}}>{t.bdm_name}</div>
                    <div className="text-sm text-slate-500 mt-1">{t.bde_count} BDE{t.bde_count !== 1 ? 's' : ''} · {t.active_bde_count} active</div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {t.bdes.slice(0, 4).map((m) => (
                        <span key={m.user_id} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-1">{m.name}</span>
                      ))}
                    </div>
                    {isTeamAdmin && (
                      <button data-testid={`bdm-team-manage-btn-${t.bdm_user_id}`}
                        onClick={(e) => { e.stopPropagation(); setManageTeamBdm(t); }}
                        className="mt-4 text-xs uppercase tracking-wider font-bold text-emerald-700 hover:text-emerald-900">
                        Manage Team →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isTeamAdmin && teams.length > 0 && (
              <div className="relative inline-block mb-4">
                <select data-testid="bdm-dashboard-selector" value={selectedBdmId} onChange={(e) => setSelectedBdmId(e.target.value)}
                  className="rounded-none bg-white border border-slate-300 pl-3 pr-8 py-2 text-sm outline-none appearance-none">
                  {teams.map((t) => <option key={t.bdm_user_id} value={t.bdm_user_id}>View: {t.bdm_name}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            )}

            {dashboardLoading ? (
              <div className="flex justify-center py-8"><div className="loading-spinner"></div></div>
            ) : bdmDashboard && (
              <div data-testid="bdm-dashboard" className="bg-white border border-slate-200 p-6 space-y-6">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{bdmDashboard.bdm.name}'s Team</div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    ["Total Leads", bdmDashboard.leads.total],
                    ["Follow-ups Due", bdmDashboard.leads.follow_ups_due_today],
                    ["Overdue", bdmDashboard.leads.overdue],
                    ["Dormant Leads", bdmDashboard.leads.dormant],
                  ].map(([label, val]) => (
                    <div key={label} className="border border-slate-100 p-3">
                      <div className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: '#002FA7'}}>{val}</div>
                      <div className="text-xs text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(bdmDashboard.deals.by_stage).map(([stage, count]) => (
                    <div key={stage} className="border border-slate-100 p-3">
                      <div className="text-xl font-bold">{count}</div>
                      <div className="text-xs text-slate-500 capitalize">{stage.replace('_', ' ')}</div>
                    </div>
                  ))}
                  <div className="border border-slate-100 p-3 bg-emerald-50">
                    <div className="text-xl font-bold text-emerald-700">₹{bdmDashboard.deals.pipeline_value.toLocaleString('en-IN')}</div>
                    <div className="text-xs text-emerald-700">Pipeline Value</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="border border-slate-100 p-3">
                    <div className="text-lg font-bold">{bdmDashboard.sales.quotations_count} (₹{bdmDashboard.sales.quotations_value.toLocaleString('en-IN')})</div>
                    <div className="text-xs text-slate-500">Quotations</div>
                  </div>
                  <div className="border border-slate-100 p-3">
                    <div className="text-lg font-bold">{bdmDashboard.sales.sales_orders_count} (₹{bdmDashboard.sales.sales_orders_value.toLocaleString('en-IN')})</div>
                    <div className="text-xs text-slate-500">Sales Orders</div>
                  </div>
                  <div className="border border-slate-100 p-3">
                    <div className="text-lg font-bold">{bdmDashboard.sales.invoices_count} (₹{bdmDashboard.sales.invoices_value.toLocaleString('en-IN')})</div>
                    <div className="text-xs text-slate-500">Invoices</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Team Performance</div>
                  <table data-testid="bdm-team-performance-table" className="w-full text-sm border border-slate-200">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-2">BDE</th>
                        <th className="text-right p-2">Leads</th>
                        <th className="text-right p-2">New</th>
                        <th className="text-right p-2">Active Deals</th>
                        <th className="text-right p-2">Pipeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bdmDashboard.leads.per_bde.map((l) => {
                        const d = bdmDashboard.deals.per_bde.find((x) => x.name === l.name) || {};
                        return (
                          <tr key={l.name} className="border-b border-slate-100">
                            <td className="p-2 font-medium">{l.name}</td>
                            <td className="p-2 text-right">{l.total_leads}</td>
                            <td className="p-2 text-right">{l.new_leads}</td>
                            <td className="p-2 text-right">{d.active_deals || 0}</td>
                            <td className="p-2 text-right">₹{(d.pipeline_value || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : data && (
          <>
            {/* Team Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Total Agents", value: data.team_stats.total_agents, icon: Users, color: "#002FA7" },
                { label: "Total Revenue", value: `₹${data.team_stats.total_revenue.toLocaleString('en-IN')}`, icon: CurrencyDollar, color: "#10B981" },
                { label: "Deals Won", value: data.team_stats.total_deals_won, icon: Trophy, color: "#F59E0B" },
                { label: "Avg Revenue/Agent", value: `₹${data.team_stats.avg_revenue_per_agent.toLocaleString('en-IN')}`, icon: ChartBar, color: "#DC2626" },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                    <Icon size={28} weight="duotone" style={{color: s.color}} className="mb-3" />
                    <div className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: s.color}}>{s.value}</div>
                    <div className="text-sm text-slate-600 mt-1">{s.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Revenue Chart */}
            <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6 mb-8">
              <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Revenue by Agent</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.leaderboard}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="name" stroke="#475569" />
                  <YAxis stroke="#475569" />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Leaderboard Table */}
            <div data-testid="leaderboard-table" className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-y border-slate-200">
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Rank</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Agent</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Role</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Revenue</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Deals Won</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Win Rate</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Activities</th>
                    <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leaderboard.map((agent, idx) => (
                    <tr key={agent.user_id} data-testid={`agent-row-${idx}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4 font-bold text-lg" style={{color: getRankColor(idx)}}>
                        {getRankLabel(idx)}
                      </td>
                      <td className="p-4 font-medium">{agent.name}</td>
                      <td className="p-4"><span className="rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">{agent.role}</span></td>
                      <td className="p-4 font-bold text-[#002FA7]">₹{agent.revenue.toLocaleString('en-IN')}</td>
                      <td className="p-4">{agent.deals_won}/{agent.deals_total}</td>
                      <td className="p-4">{agent.win_rate}%</td>
                      <td className="p-4">{agent.activities}</td>
                      <td className="p-4">{agent.contacts_managed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {manageTeamBdm && (
          <ManageTeamModal bdm={manageTeamBdm} onClose={() => { setManageTeamBdm(null); fetchTeams(); }} />
        )}
      </div>
    </div>
  );
};

export default TeamPerformance;
